'use client'

// Next Imports
import { useState } from 'react'

import Link from 'next/link'
import { useParams } from 'next/navigation'

// MUI Imports
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

// React-Toastify
import { toast } from 'react-toastify'

// Third-party Imports
import { valibotResolver } from '@hookform/resolvers/valibot'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import type { InferInput } from 'valibot'
import { email, nonEmpty, object, pipe, string } from 'valibot'

import { CircularProgress } from '@mui/material'

import type { getDictionary } from '@/utils/getDictionary'

// Type Imports
import type { Locale } from '@configs/i18n'

// Component Imports
import DirectionalIcon from '@components/DirectionalIcon'
import CustomTextField from '@core/components/mui/TextField'

// Util Imports
import { getLocalizedUrl } from '@/utils/i18n'
import AuthClient from '@/libs/axiosInterceptors'

type Props = {
  dictionary: Awaited<ReturnType<typeof getDictionary>>
}

const ForgotPassword = ({ dictionary }: Props) => {
  // Hooks
  const { lang: locale } = useParams()
  const [isLoading, setIsLoading] = useState(false)

  // Define validation schema
  const schema = object({
    email: pipe(string(), nonEmpty(dictionary.auth.email_required), email(dictionary.auth.valid_email))
  })

  type FormData = InferInput<typeof schema>

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: valibotResolver(schema),
    defaultValues: {
      email: ''
    }
  })

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    try {
      setIsLoading(true)

      const response = await AuthClient.post('/account/forgot-password', {
        email: data.email
      })

      if (response.data?.status) {
        toast.success(response.data?.message)
        reset()
      } else {
        throw new Error(response.data?.error)
      }
    } catch (error: any) {
      let errorMessage = ''

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='flex bs-full items-center justify-center flex-col gap-6'>
      <div className='z-[3] w-[280px] h-auto sm:w-[316.55px] sm:h-[60px]'>
        <img src='/images/logo.png' alt='Logo' className='w-full h-full' />
      </div>

      <div className='z-10 flex justify-center items-center bg-backgroundPaper backdrop-blur-[15px] w-[320px] sm:w-[384px] h-auto px-6 py-7 gap-6 rounded-lg'>
        <div className='flex flex-col gap-6 is-full'>
          <div className='flex flex-col gap-1'>
            <Typography variant='h4' className='text-white text-xl sm:text-[24px] font-medium leading-[38px]'>
              {dictionary.auth.forgot_password} 🔒
            </Typography>
            <Typography className='text-white text-sm sm:text-[15px] font-normal leading-[22px]'>
              {dictionary.auth.reset_instructions}
            </Typography>
          </div>
          <form noValidate autoComplete='off' onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
            <Controller
              name='email'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  autoFocus
                  fullWidth
                  type='email'
                  label={
                    <Typography className='text-white text-[13px] font-normal leading-[15px]'>
                      {dictionary.auth.email}
                    </Typography>
                  }
                  placeholder={dictionary.auth.enter_email}
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />
            <Button
              fullWidth
              variant='contained'
              type='submit'
              className='bg-gradient-custom shadow-none'
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} className='text-white' /> : dictionary.auth.send_reset_link}
            </Button>
          </form>
        </div>
      </div>
      <Typography className='flex justify-center items-center text-white z-[3]' color='primary.main'>
        <Link href={getLocalizedUrl('/login', locale as Locale)} className='flex items-center gap-1.5'>
          <DirectionalIcon ltrIconClass='tabler-chevron-left' rtlIconClass='tabler-chevron-right' className='text-xl' />
          <span>{dictionary.auth.back_to_login}</span>
        </Link>
      </Typography>
    </div>
  )
}

export default ForgotPassword
